import React from 'react';
import { color } from './ui/theme';

/**
 * App-wide error boundary. Without one, any error thrown during render (a
 * failed asset load surfacing through Suspense, a bad prop, etc.) makes React
 * 18 unmount the WHOLE tree — the page shows for a frame and then goes blank.
 *
 * This catches that, keeps the app mounted, and DISPLAYS the error (message +
 * stack + component stack) so failures are diagnosable in production too —
 * critical on the deployed site where the dev console isn't reachable.
 */
interface State { error: Error | null; info: React.ErrorInfo | null; }

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep it in the console too (useful where the console IS reachable).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, info?.componentStack);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={wrap}>
        <div style={card}>
          <div style={title}>⚠ Something crashed</div>
          <div style={sub}>
            The app hit an unexpected error. Details below (screenshot this if you
            need help):
          </div>
          <div style={label}>{error.name}: {error.message}</div>
          {error.stack && <pre style={pre}>{error.stack}</pre>}
          {info?.componentStack && (
            <>
              <div style={label}>Component stack</div>
              <pre style={pre}>{info.componentStack}</pre>
            </>
          )}
          <button style={btn} onClick={() => window.location.reload()}>
            ↻ Reload
          </button>
        </div>
      </div>
    );
  }
}

const wrap: React.CSSProperties = {
  position: 'fixed', inset: 0, overflow: 'auto', padding: 20,
  background: 'linear-gradient(160deg,#0a0a14,#150a22)',
  color: color.ink, fontFamily: "'Montserrat', system-ui, sans-serif",
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
};
const card: React.CSSProperties = {
  maxWidth: 760, width: '100%', marginTop: 40,
  background: 'rgba(20,16,40,0.7)', border: '1px solid rgba(239,68,68,0.4)',
  borderRadius: 14, padding: 22,
};
const title: React.CSSProperties = { fontSize: 22, fontWeight: 800, color: '#FCA5A5' };
const sub: React.CSSProperties = { fontSize: 14, color: '#C9C2EA', margin: '8px 0 16px' };
const label: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: color.accentSoft, margin: '14px 0 6px' };
const pre: React.CSSProperties = {
  fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(120,80,220,0.25)',
  borderRadius: 8, padding: 12, color: '#E9D5FF', maxHeight: 320, overflow: 'auto',
};
const btn: React.CSSProperties = {
  marginTop: 18, background: 'linear-gradient(135deg,#7c3aed,#22d3ee)', color: '#fff',
  border: 'none', borderRadius: 12, padding: '12px 26px', fontSize: 15, fontWeight: 800, cursor: 'pointer',
};
